import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import atelierApi from '../services/atelier';
import api from '../services/api';
import carnetCommandeApi from '../services/carnet-commande';
import { formatDateFr } from '../utils/planningTime';
import InlineStatusSelect from './InlineStatusSelect';
import './AtelierView.css';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';

// Mapping des en-têtes Excel vers les champs internes
const HEADER_MAP = {
  'date demande':      'date_demande',
  'type':              'type',
  'dépositaire':       'nom_depositaire',
  'depositaire':       'nom_depositaire',
  'marque':            'marque',
  'modèle':            'modele',
  'modele':            'modele',
  'immat':             'immatriculation',
  'immatriculation':   'immatriculation',
  'demande travaux':   'demande_travaux',
  'prestataire':       'prestataire',
  'n° or':             'numero_or',
  'n or':              'numero_or',
  'numero or':         'numero_or',
  'date fin':          'date_fin_travaux',
  'statut':            'statut',
  'commentaire':       'commentaire',
  'date revue':        'date_revue',
  'concession':        'concession',
};

function AtelierView({ user, editMode = false, setHeaderAction }) {
  // Colonne Planification : le dossier s'ouvre ici, sans quitter le suivi.
  const { ouvrir: ouvrirDossier, modale: modaleDossier } = useDossierPlanning({
    user, editMode, onEnregistre: () => loadData(),
  });
  const isOperateur = user?.role === 'operateur';
  const isSuperAdmin = user?.role === 'super-admin';

  const [vehicules, setVehicules]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [filterStatut, setFilterStatut]     = useState('');
  const [filterType, setFilterType]         = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [filterPrestataire, setFilterPrestataire] = useState('');
  const [search, setSearch]                 = useState('');
  const [view, setView]                     = useState('actives');
  const [archives, setArchives]             = useState([]);
  const [parkingMap, setParkingMap]          = useState({});
  const [facturesBadgeMap, setFacturesBadgeMap] = useState({});
  const [factureDetail, setFactureDetail]   = useState(null);

  // Boutons d'action dans le header
  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction({
        primary: { label: '+ Ajouter un véhicule', onClick: () => handleOpenAdd('VN') },
        secondary: [
          { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Import</>, onClick: () => { setImportResult(null); setImportRows([]); setImportError(''); setImportFilename(''); setShowImportModal(true); } },
          { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export</>, onClick: () => handleExport() },
        ]
      });
    }
  }, [setHeaderAction]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showModal, setShowModal]           = useState(false);
  const [commandeDetailOr, setCommandeDetailOr]     = useState(null);
  const [commandeDetailData, setCommandeDetailData] = useState(null);
  const [commandeDetailLoading, setCommandeDetailLoading] = useState(false);
  const [modalType, setModalType]           = useState('');
  const [selectedVehicule, setSelectedVehicule] = useState(null);
  const [commentaires, setCommentaires]     = useState([]);
  const [newCommentaire, setNewCommentaire] = useState('');
  const [formData, setFormData]             = useState({
    type: '',
    nom_depositaire: '',
    marque: '',
    modele: '',
    immatriculation: '',
    demande_travaux: '',
    prestataire: '',
    numero_or: '',
    date_demande: '',
    date_fin_travaux: '',
    statut: 'A planifier',
    commentaire: '',
    date_revue: '',
    concession_id: ''
  });

  // Concessions
  const [concessions, setConcessions] = useState([]);

  // Import Excel
  const [showImportModal, setShowImportModal] = useState(false);
  const [importRows, setImportRows]           = useState([]);
  const [importFilename, setImportFilename]   = useState('');
  const [importLoading, setImportLoading]     = useState(false);
  const [importError, setImportError]         = useState('');
  const [importResult, setImportResult]       = useState(null);
  const importFileRef                         = useRef(null);

  const statuts = ['A planifier', 'Planifié', 'Pièces en commande', 'Immobilisé', 'Terminé', 'Restitué'];

  const getStatutColor = (statut) => {
    const colors = {
      'A planifier':          '#a855f7',
      'Planifié':             '#3b82f6',
      'Pièces en commande':   '#eab308',
      'Immobilisé':           '#f97316',
      'Terminé':              '#10b981',
      'Restitué':             '#6b7280'
    };
    return colors[statut] || '#6b7280';
  };

  // Chargement données + concessions
  useEffect(() => {
    loadData();
  }, [filterStatut, filterType, search]); // eslint-disable-line

  useEffect(() => {
    if (!isOperateur) {
      api.get('/parking/concessions')
        .then(res => setConcessions(res.data.concessions || []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterType)   params.type   = filterType;
      if (search)       params.search  = search;
      const response = await atelierApi.getAll(params);
      const vList = response.data.vehicules || [];
      setVehicules(vList);
      const immatList = [...new Set(vList.map(v => v.immatriculation).filter(Boolean))];
      if (immatList.length > 0) {
        api.post('/parking/check-immat-batch', { immat: immatList })
          .then(r => setParkingMap(r.data.found || {}))
          .catch(() => {});
        const orList = [...new Set(vList.map(v => v.numero_or).filter(Boolean))];
        if (orList.length > 0) {
          api.post('/factures-services/by-or-batch', { ors: orList })
            .then(r => setFacturesBadgeMap(r.data.factures || {}))
            .catch(() => {});
        }
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = (type) => {
    setModalType(`add-${type.toLowerCase()}`);
    setSelectedVehicule(null);
    setFormData({
      type,
      nom_depositaire: '',
      marque: '',
      modele: '',
      immatriculation: '',
      demande_travaux: '',
      prestataire: '',
      numero_or: '',
      date_demande: new Date().toISOString().split('T')[0],
      date_fin_travaux: '',
      statut: 'A planifier',
      commentaire: '',
        date_revue: '',
      concession_id: user.concession_id || (concessions[0]?.id ?? '')
    });
    setShowModal(true);
  };

  const handleOpenEdit = (vehicule) => {
    setModalType('edit');
    setSelectedVehicule(vehicule);
    setFormData({
      type:                vehicule.type,
      nom_depositaire:     vehicule.nom_depositaire || '',
      marque:              vehicule.marque || '',
      modele:              vehicule.modele || '',
      immatriculation:     vehicule.immatriculation || '',
      demande_travaux:     vehicule.demande_travaux || '',
      prestataire:         vehicule.prestataire || '',
      numero_or:           vehicule.numero_or || '',
      date_demande:        vehicule.date_demande?.split('T')[0] || '',
      date_fin_travaux:    vehicule.date_fin_travaux?.split('T')[0] || '',
      statut:              vehicule.statut || 'Planifié',
      commentaire:         vehicule.commentaire || '',
      date_revue:          vehicule.date_revue?.split('T')[0] || '',
      concession_id:       vehicule.concession_id || ''
    });
    setShowModal(true);
  };

  const handleOpenView = async (vehicule) => {
    setModalType('view');
    setSelectedVehicule(vehicule);
    setShowModal(true);
    loadCommentaires(vehicule.id);
  };

  const loadCommentaires = async (vehiculeId) => {
    try {
      const response = await atelierApi.getCommentaires(vehiculeId);
      setCommentaires(response.data.commentaires || []);
    } catch (err) {
      console.error('Erreur chargement commentaires:', err);
      setCommentaires([]);
    }
  };

  const handleAddCommentaire = async () => {
    if (!newCommentaire.trim()) return;
    try {
      await atelierApi.addCommentaire(selectedVehicule.id, { commentaire: newCommentaire });
      setNewCommentaire('');
      loadCommentaires(selectedVehicule.id);
    } catch (err) {
      console.error('Erreur ajout commentaire:', err);
      alert('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleUpdateStatutInline = async (id, newStatut) => {
    try {
      await atelierApi.update(id, { statut: newStatut });
      setVehicules(prev => prev.map(v => v.id === id ? { ...v, statut: newStatut } : v));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'edit') {
        await atelierApi.update(selectedVehicule.id, formData);
        alert('Véhicule modifié avec succès');
      } else {
        await atelierApi.create(formData);
        alert('Véhicule ajouté avec succès');
      }
      setShowModal(false);
      setSelectedVehicule(null);
      loadData();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce véhicule ? Cette action est irréversible.')) return;
    try {
      await atelierApi.delete(id);
      loadData();
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Archiver ce véhicule ?')) return;
    try {
      await atelierApi.delete(id);
      alert('Véhicule archivé');
      loadData();
    } catch (err) {
      console.error('Erreur archivage:', err);
      alert('Erreur lors de l\'archivage');
    }
  };

  /* ── Export Excel (.xlsx) ── */
  const handleExport = async () => {
    try {
      const response = await atelierApi.exportExcel();
      const data = response.data.data;

      const labelPieces = (s) => s === 'livre' ? 'Reçu' : s === 'recu_partiellement' ? 'Partiel' : s === 'en_commande' ? 'Commandé' : '';
      const wsData = [
        ['Date demande', 'Type', 'Dépositaire', 'Marque', 'Modèle', 'Immat',
         'Demande travaux', 'Prestataire', 'N° OR', 'Pièces', 'Date fin', 'Statut',
         'Commentaire', 'Intervenant', 'Date revue', 'Concession'],
        ...data.map(row => [
          row.date_demande   ? new Date(row.date_demande).toLocaleDateString('fr-FR')   : '',
          row.type           || '',
          row.nom_depositaire|| '',
          row.marque         || '',
          row.modele         || '',
          row.immatriculation|| '',
          row.demande_travaux|| '',
          row.prestataire    || '',
          row.numero_or      || '',
          labelPieces(row.statut_commande),
          row.date_fin_travaux ? formatDateFr(row.date_fin_travaux) : '',
          row.statut         || '',
          row.commentaire    || '',
          row.date_revue     ? formatDateFr(row.date_revue) : '',
          row.concession     || ''
        ])
      ];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [10,8,18,12,12,12,30,14,10,10,10,18,30,12,10,18].map(w => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, 'Atelier');
      XLSX.writeFile(wb, `atelier_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Erreur export:', err);
      alert('Erreur lors de l\'export');
    }
  };

  /* ── Télécharger modèle d'import ── */
  const handleDownloadTemplate = () => {
    const wsData = [
      ['Type', 'Dépositaire', 'Marque', 'Modèle', 'Immat', 'Demande travaux',
       'Prestataire', 'N° OR', 'Date demande', 'Date fin', 'Statut',
       'Commentaire', 'Intervenant', 'Date revue', 'Concession'],
      ['VN', 'Dupont', 'Peugeot', '308', 'AB-123-CD', 'Révision + CT',
       '', '', new Date().toLocaleDateString('fr-FR'), '', 'Planifié',
       '', '', '', 'Autodom 97']
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [10,18,12,12,12,30,14,10,12,10,18,30,12,10,18].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    XLSX.writeFile(wb, 'modele_import_atelier.xlsx');
  };

  /* ── Import Excel ── */
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportRows([]);
    setImportFilename('');
    setImportError('');
    setImportResult(null);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFilename(file.name);
    setImportError('');
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (raw.length < 2) { setImportError('Fichier vide ou sans données.'); return; }

        const headers = raw[0].map(h => String(h).toLowerCase().trim());
        const rows = [];

        for (let i = 1; i < raw.length; i++) {
          const rowArr = raw[i];
          if (rowArr.every(v => !v)) continue; // ligne vide
          const obj = {};
          headers.forEach((h, idx) => {
            const field = HEADER_MAP[h];
            if (!field) return;
            let val = rowArr[idx];
            // Convertir dates Excel en chaîne ISO si nécessaire
            if (val instanceof Date) {
              val = val.toISOString().split('T')[0];
            } else if (typeof val === 'number' && h.includes('date')) {
              // numéro de série Excel
              const d = new Date(Math.round((val - 25569) * 86400 * 1000));
              val = d.toISOString().split('T')[0];
            } else {
              val = val !== undefined && val !== null ? String(val).trim() : '';
            }
            obj[field] = val;
          });
          rows.push(obj);
        }

        setImportRows(rows);
      } catch (err) {
        setImportError('Impossible de lire le fichier : ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImportConfirm = async () => {
    if (importRows.length === 0) return;
    setImportLoading(true);
    setImportError('');
    try {
      const res = await atelierApi.importExcel(importRows);
      setImportResult(res.data);
      loadData();
    } catch (err) {
      setImportError(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setImportLoading(false);
    }
  };

  /* ── Helpers ── */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Détail de la commande de pièces (popup au clic sur le badge Pièces)
  useEffect(() => {
    if (!commandeDetailOr) { setCommandeDetailData(null); return; }
    setCommandeDetailLoading(true);
    carnetCommandeApi.getByOr(commandeDetailOr)
      .then(res => setCommandeDetailData(res.data))
      .catch(() => setCommandeDetailData(null))
      .finally(() => setCommandeDetailLoading(false));
  }, [commandeDetailOr]);

  const colSpan = 12;

  return (
    <div className="page-container">
      {/* ONGLETS */}
      <div className="page-tabs">
        <button className={view === 'actives' ? 'active' : ''} onClick={() => setView('actives')}>
          📋 Véhicules atelier <span className="tab-count">{vehicules.length}</span>
        </button>
        <button className={view === 'archives' ? 'active' : ''} onClick={() => { setView('archives'); atelierApi.getArchives().then(r => setArchives(r.data.vehicules || [])).catch(() => {}); }}>
          🗄️ Archives
        </button>
      </div>

      {view === 'actives' && <>
      {/* FILTRES */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select className={`page-select${filterStatut ? ' active-filter' : ''}`} value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {statuts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select className={`page-select${filterType ? ' active-filter' : ''}`} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">Tous types</option>
          <option value="VN">VN</option>
          <option value="VO">VO</option>
          <option value="Loueurs">Loueurs</option>
        </select>

        <select className={`page-select${filterPrestataire ? ' active-filter' : ''}`} value={filterPrestataire} onChange={(e) => setFilterPrestataire(e.target.value)}>
          <option value="">Tous prestataires</option>
          {[...new Set(vehicules.map(v => v.prestataire).filter(Boolean))].sort().map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {isSuperAdmin && concessions.length > 1 && (
          <select className={`page-select${filterConcession ? ' active-filter' : ''}`} value={filterConcession} onChange={(e) => setFilterConcession(e.target.value)}>
            <option value="">Toutes concessions</option>
            {concessions.map(c => typeof c === 'string' ? <option key={c} value={c}>{c}</option> : <option key={c.id || c.nom} value={c.nom}>{c.nom}</option>)}
          </select>
        )}
      </div>

      {loading && <div className="loading">⏳ Chargement...</div>}

      {error && (
        <div style={{ padding: '20px', background: '#fee', color: '#c00', borderRadius: '8px', margin: '20px' }}>
          ❌ Erreur : {error}
        </div>
      )}

      {/* TABLEAU */}
      {!loading && !error && (
        <div className="table-container atelier-table-scroll">
          <table className="atelier-table">
            <thead>
              <tr>
                <th>Date demande</th>
                <th>Type</th>
                <th>Prestataire</th>
                <th>Marque / Modèle</th>
                <th>Immat</th>
                <th style={{ textAlign: 'center', width: '1%', padding: '4px' }} title="Gestion Parking">🅿️</th>
                <th>N° OR</th>
                <th>Planification</th>
                <th>Pièces</th>
                <th>Date fin</th>
                <th>Date facture</th>
                <th>Statut</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let filtered = vehicules;
                if (filterConcession) filtered = filtered.filter(v => v.concession_nom === filterConcession);
                if (filterPrestataire) filtered = filtered.filter(v => v.prestataire === filterPrestataire);
                return filtered.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                    Aucun véhicule trouvé
                  </td>
                </tr>
              ) : (
                filtered.map(vehicule => (
                  <tr key={vehicule.id} style={{ cursor: 'pointer' }} onClick={() => handleOpenView(vehicule)}>
                    <td>{formatDate(vehicule.date_demande)}</td>
                    <td>
                      <span className={`badge-${vehicule.type?.toLowerCase()}`}>{vehicule.type}</span>
                    </td>
                    <td>{vehicule.prestataire || '—'}</td>
                    <td>
                      <div style={{ fontWeight: '600' }}>{vehicule.marque}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{vehicule.modele}</div>
                    </td>
                    <td style={{ fontWeight: '700' }}>{vehicule.immatriculation}</td>
                    <td style={{ textAlign: 'center', padding: '4px' }}>
                      {vehicule.immatriculation && parkingMap[(vehicule.immatriculation || '').replace(/[-\s]/g, '').toUpperCase()] ? (
                        <span style={{ fontSize: '16px' }} title="Véhicule au parking">🅿️</span>
                      ) : null}
                    </td>
                    <td>{vehicule.numero_or}</td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <PlanificationCell dossier={vehicule} onOuvrir={ouvrirDossier} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {vehicule.statut_commande ? (
                        <span
                          style={{
                            cursor: 'pointer', display: 'inline-block', whiteSpace: 'nowrap',
                            background: vehicule.statut_commande === 'livre' ? '#dcfce7' : vehicule.statut_commande === 'recu_partiellement' ? '#fef3c7' : '#dbeafe',
                            color: vehicule.statut_commande === 'livre' ? '#15803d' : vehicule.statut_commande === 'recu_partiellement' ? '#b45309' : '#1d4ed8',
                            border: `1px solid ${vehicule.statut_commande === 'livre' ? '#bbf7d0' : vehicule.statut_commande === 'recu_partiellement' ? '#fde68a' : '#bfdbfe'}`,
                            fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600
                          }}
                          title="Cliquer pour le détail de la commande"
                          onClick={() => { if (vehicule.numero_or) setCommandeDetailOr(vehicule.numero_or); }}
                        >
                          {vehicule.statut_commande === 'livre' ? '✓ Reçu' : vehicule.statut_commande === 'recu_partiellement' ? '◐ Partiel' : '⏳ Commandé'}
                          {vehicule.nb_anomalies > 0 && <span title={`${vehicule.nb_anomalies} anomalie(s) active(s)`}> ⚠️</span>}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      {formatDate(vehicule.date_fin_travaux)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {(() => {
                        const fd = facturesBadgeMap[(vehicule.numero_or || '').trim().toUpperCase()];
                        if (!fd?.date_facture) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                            <span style={{ fontSize: 12 }}>{new Date(fd.date_facture).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                            <span className="alert-badge badge-facture badge-facture-clickable" title="Voir la dernière facture" style={{ cursor: 'pointer' }} onClick={() => setFactureDetail(fd)}>FAC</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <InlineStatusSelect
                        value={vehicule.statut}
                        options={statuts.map(s => ({ value: s, label: s, color: getStatutColor(s) }))}
                        onChange={(newStatut) => handleUpdateStatutInline(vehicule.id, newStatut)}
                      />
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="actions-cell">
                        <button onClick={() => handleOpenEdit(vehicule)} className="action-btn edit" title="Modifier">✏️</button>
                        <button onClick={() => handleArchive(vehicule.id)} className="action-btn delete" title="Archiver">✅</button>
                        {isSuperAdmin && (
                          <button onClick={() => handleDelete(vehicule.id)} className="action-btn delete" title="Supprimer définitivement" style={{ color: 'var(--color-error)' }}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ); })()}
            </tbody>
          </table>
        </div>
      )}

      </>}

      {view === 'archives' && (
        <div className="table-container atelier-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date archivage</th>
                <th>Type</th>
                <th>Prestataire</th>
                <th>Marque / Modèle</th>
                <th>Immat</th>
                <th>N° OR</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {archives.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Aucun véhicule archivé</td></tr>
              ) : archives.map(v => (
                <tr key={v.id}>
                  <td>{v.date_archivage ? new Date(v.date_archivage).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}</td>
                  <td><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: v.type === 'VN' ? '#dbeafe' : v.type === 'VO' ? '#fef3c7' : '#e0e7ff', color: v.type === 'VN' ? '#1d4ed8' : v.type === 'VO' ? '#92400e' : '#4338ca' }}>{v.type}</span></td>
                  <td>{v.prestataire || '—'}</td>
                  <td>{v.marque} {v.modele}</td>
                  <td style={{ fontWeight: 700 }}>{v.immatriculation}</td>
                  <td>{v.numero_or || '—'}</td>
                  <td><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#f3f4f6', color: '#6b7280' }}>{v.statut}</span></td>
                  <td>
                    <button className="btn-icon" title="Désarchiver" onClick={async () => { await atelierApi.unarchive(v.id); setArchives(prev => prev.filter(a => a.id !== v.id)); loadData(); }}>↩️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL AJOUT/MODIFICATION */}
      {showModal && (modalType === 'add-vn' || modalType === 'add-vo' || modalType === 'add-loueurs' || modalType === 'edit') && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalType === 'edit' ? 'Modifier le véhicule' : `Ajouter un ${formData.type}`}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">

                  <div className="form-group">
                    <label>Nom dépositaire *</label>
                    <input type="text" value={formData.nom_depositaire}
                      onChange={(e) => setFormData({...formData, nom_depositaire: e.target.value})}
                      required placeholder="Nom du dépositaire" />
                  </div>

                  <div className="form-group">
                    <label>Type *</label>
                    <select value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})} required>
                      <option value="VN">VN</option>
                      <option value="VO">VO</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Marque *</label>
                    <input type="text" value={formData.marque}
                      onChange={(e) => setFormData({...formData, marque: e.target.value})}
                      required placeholder="Ex: Peugeot, Renault..." />
                  </div>

                  <div className="form-group">
                    <label>Modèle *</label>
                    <input type="text" value={formData.modele}
                      onChange={(e) => setFormData({...formData, modele: e.target.value})}
                      required placeholder="Ex: 308, Clio..." />
                  </div>

                  <div className="form-group">
                    <label>Immatriculation *</label>
                    <input type="text" value={formData.immatriculation}
                      onChange={(e) => setFormData({...formData, immatriculation: e.target.value.replace(/[\s-]/g, '').toUpperCase()})}
                      placeholder="AB123CD" required />
                  </div>

                  <div className="form-group">
                    <label>Date demande *</label>
                    <input type="date" value={formData.date_demande}
                      onChange={(e) => setFormData({...formData, date_demande: e.target.value})}
                      required />
                  </div>

                  {!isOperateur && (
                    <div className="form-group">
                      <label>Concession</label>
                      <select value={formData.concession_id}
                        onChange={(e) => setFormData({...formData, concession_id: e.target.value})}>
                        <option value="">— Sélectionner —</option>
                        {concessions.map(c => (
                          <option key={c.id} value={c.id}>{c.nom}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group full-width">
                    <label>Demande de travaux *</label>
                    <textarea value={formData.demande_travaux}
                      onChange={(e) => setFormData({...formData, demande_travaux: e.target.value})}
                      rows="3" placeholder="Description des travaux à effectuer..." required />
                  </div>

                  <div className="form-group">
                    <label>Prestataire</label>
                    <input type="text" value={formData.prestataire}
                      onChange={(e) => setFormData({...formData, prestataire: e.target.value})}
                      placeholder="Nom du prestataire (optionnel)" />
                  </div>

                  <div className="form-group">
                    <label>N° OR</label>
                    <input type="text" value={formData.numero_or}
                      onChange={(e) => setFormData({...formData, numero_or: e.target.value})}
                      placeholder="Numéro d'ordre de réparation (optionnel)" />
                  </div>

                  <div className="form-group">
                    <label>Date fin travaux prévue</label>
                    <input type="date" value={formData.date_fin_travaux}
                      onChange={(e) => setFormData({...formData, date_fin_travaux: e.target.value})} />
                  </div>

                  <div className="form-group">
                    <label>Statut *</label>
                    <select value={formData.statut}
                      onChange={(e) => setFormData({...formData, statut: e.target.value})} required>
                      {statuts.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Date revue</label>
                    <input type="date" value={formData.date_revue}
                      onChange={(e) => setFormData({...formData, date_revue: e.target.value})} />
                  </div>

                  <div className="form-group full-width">
                    <label>Commentaire</label>
                    <textarea value={formData.commentaire}
                      onChange={(e) => setFormData({...formData, commentaire: e.target.value})}
                      rows="2" placeholder="Commentaire optionnel..." />
                  </div>
                </div>

                <p style={{ marginTop: '20px', fontSize: '13px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  * Champs obligatoires
                </p>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Annuler</button>
                <button type="submit" className="btn-primary">
                  {modalType === 'edit' ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DÉTAILS */}
      {showModal && modalType === 'view' && selectedVehicule && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className={`badge-${selectedVehicule.type?.toLowerCase()}`}>{selectedVehicule.type}</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{selectedVehicule.immatriculation}</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{selectedVehicule.marque} {selectedVehicule.modele}</span>
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              {/* Barre résumé */}
              <div className="atelier-summary-bar">
                <div className="atelier-summary-item">
                  <span className="atelier-summary-label">Statut</span>
                  <span className="statut-badge" style={{ backgroundColor: getStatutColor(selectedVehicule.statut) }}>
                    {selectedVehicule.statut}
                  </span>
                </div>
                <div className="atelier-summary-item">
                  <span className="atelier-summary-label">Demandé le</span>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>{formatDate(selectedVehicule.date_demande)}</span>
                </div>
                <div className="atelier-summary-item">
                  <span className="atelier-summary-label">Fin prévue</span>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>
                    {formatDate(selectedVehicule.date_fin_travaux) || '—'}
                  </span>
                </div>
                <div className="atelier-summary-item">
                  <span className="atelier-summary-label">Date revue</span>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>{formatDate(selectedVehicule.date_revue) || '—'}</span>
                </div>
              </div>

              {/* Grille infos 2 colonnes */}
              <div className="atelier-detail-grid">
                <div className="atelier-detail-col">
                  <div className="atelier-field"><span>Dépositaire</span><strong>{selectedVehicule.nom_depositaire || '—'}</strong></div>
                  <div className="atelier-field"><span>Prestataire</span><strong>{selectedVehicule.prestataire || '—'}</strong></div>
                  <div className="atelier-field"><span>N° OR</span><strong>{selectedVehicule.numero_or || '—'}</strong></div>
                  {!isOperateur && <div className="atelier-field"><span>Concession</span><strong>{selectedVehicule.concession_nom || '—'}</strong></div>}
                </div>
                <div className="atelier-detail-col">
                  {selectedVehicule.commentaire && (
                    <div className="atelier-field"><span>Commentaire</span><strong>{selectedVehicule.commentaire}</strong></div>
                  )}
                </div>
              </div>

              {/* Bloc demande travaux */}
              <div className="atelier-demande-block">
                <div className="atelier-demande-label">Demande de travaux</div>
                <div className="atelier-demande-text">{selectedVehicule.demande_travaux || '—'}</div>
              </div>

              {/* Commentaires */}
              <div className="historique-section" style={{ marginTop: '14px', borderTop: '2px solid var(--border-default)', paddingTop: '14px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' }}>💬 Commentaires</h3>
                <div className="commentaires-list" style={{ maxHeight: '160px' }}>
                  {commentaires.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '12px', fontSize: '13px' }}>Aucun commentaire</p>
                  ) : (
                    commentaires.map(c => (
                      <div key={c.id} className="commentaire-item" style={{ padding: '8px 10px', marginBottom: '6px' }}>
                        <div className="commentaire-header">
                          <strong>{c.user_prenom} {c.user_nom}</strong>
                          <span>{formatDate(c.created_at)} {new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '13px' }}>{c.commentaire}</p>
                      </div>
                    ))
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <textarea
                    value={newCommentaire}
                    onChange={(e) => setNewCommentaire(e.target.value)}
                    placeholder="Ajouter un commentaire..."
                    rows="2"
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', resize: 'none' }}
                  />
                  <button onClick={handleAddCommentaire} className="btn-primary" disabled={!newCommentaire.trim()} style={{ alignSelf: 'flex-end', padding: '8px 14px', fontSize: '13px' }}>
                    Ajouter
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowModal(false)} className="btn-secondary">Fermer</button>
              <button onClick={() => { setShowModal(false); handleOpenEdit(selectedVehicule); }} className="btn-primary">
                ✏️ Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL IMPORT EXCEL */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📥 Import Excel — Atelier</h2>
              <button className="modal-close" onClick={closeImportModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* Étape résultat */}
              {importResult ? (
                <div>
                  <div style={{ padding: '16px', background: importResult.errors?.length ? '#fef3c7' : '#dcfce7', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ fontWeight: '700', fontSize: '15px', color: importResult.errors?.length ? '#92400e' : '#166534' }}>
                      ✅ {importResult.imported} véhicule(s) importé(s)
                      {importResult.errors?.length > 0 && ` — ${importResult.errors.length} erreur(s)`}
                    </div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '12px', color: 'var(--color-error-dark)' }}>
                      {importResult.errors.map((e, i) => <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #fee2e2' }}>{e}</div>)}
                    </div>
                  )}
                  <div className="modal-actions" style={{ marginTop: '16px', paddingTop: '0', borderTop: 'none' }}>
                    <button onClick={closeImportModal} className="btn-primary">Fermer</button>
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
                    Importez un fichier Excel (.xlsx, .csv). Les colonnes doivent correspondre au modèle.
                    <button onClick={handleDownloadTemplate} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>
                      Télécharger le modèle
                    </button>
                  </p>

                  <div className="import-drop-zone">
                    <input ref={importFileRef} type="file" accept=".xlsx,.xls,.csv"
                      onChange={handleImportFile} style={{ display: 'none' }} />
                    <button className="btn-secondary" onClick={() => importFileRef.current?.click()}>
                      📂 Choisir un fichier
                    </button>
                    {importFilename && (
                      <span style={{ marginLeft: '10px', fontSize: '13px', color: 'var(--text-primary)' }}>{importFilename}</span>
                    )}
                  </div>

                  {importError && (
                    <div style={{ marginTop: '12px', padding: '10px', background: '#fee2e2', color: 'var(--color-error-dark)', borderRadius: '6px', fontSize: '13px' }}>
                      {importError}
                    </div>
                  )}

                  {importRows.length > 0 && !importError && (
                    <div style={{ marginTop: '14px', padding: '12px', background: 'var(--color-success-light)', border: '1px solid var(--color-success)', borderRadius: '8px' }}>
                      <div style={{ fontWeight: '600', color: 'var(--color-success-dark)', marginBottom: '8px' }}>
                        {importRows.length} ligne(s) détectée(s)
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)', maxHeight: '120px', overflowY: 'auto' }}>
                        {importRows.slice(0, 5).map((r, i) => (
                          <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #dcfce7' }}>
                            {r.type || '?'} · {r.nom_depositaire || '?'} · {r.immatriculation || '?'} · {r.marque || '?'} {r.modele || ''}
                          </div>
                        ))}
                        {importRows.length > 5 && <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>… et {importRows.length - 5} autre(s)</div>}
                      </div>
                    </div>
                  )}

                  <div className="modal-actions" style={{ marginTop: '20px', paddingTop: '0', borderTop: 'none' }}>
                    <button onClick={closeImportModal} className="btn-secondary">Annuler</button>
                    <button onClick={handleImportConfirm} className="btn-primary"
                      disabled={importRows.length === 0 || importLoading}>
                      {importLoading ? 'Import en cours…' : `✅ Importer ${importRows.length > 0 ? `(${importRows.length})` : ''}`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {factureDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setFactureDetail(null)}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: '10px', width: '100%', maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-default)' }}>
              <div>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>🧾 {factureDetail.num_facture}</span>
                <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {(() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })()} · OR : {factureDetail.num_or || '—'}
                </span>
              </div>
              <button onClick={() => setFactureDetail(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Client', value: factureDetail.nom_client },
                  { label: 'Immatriculation', value: factureDetail.immat },
                  { label: 'VIN', value: factureDetail.vin },
                  { label: 'Marque', value: factureDetail.marque },
                  { label: 'Société', value: factureDetail.societe },
                  { label: 'Réceptionnaire', value: factureDetail.receptionnaire },
                  { label: 'Ouverture OR', value: (() => { const d = factureDetail.date_ouverture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                  { label: 'Date facture', value: (() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                  { label: 'Montant HT total', value: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht), highlight: true },
                ].map(item => (
                  <div key={item.label} style={{ background: 'var(--bg-muted)', borderRadius: '6px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>{item.label}</div>
                    <div style={{ fontWeight: item.highlight ? 700 : 500, color: item.highlight ? 'var(--color-success)' : 'var(--text-primary)', fontSize: item.highlight ? '15px' : '13px' }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>
              {factureDetail.lignes_json && factureDetail.lignes_json.length > 0 && (<>
                <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Lignes ({factureDetail.lignes_json.length})</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead><tr style={{ background: 'var(--bg-muted)' }}>
                    {['Prestation', 'Désignation', 'Réf.', 'Qté', 'Montant HT'].map(h => <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {factureDetail.lignes_json.map((l, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 10px' }}><span style={{ background: l.prestation === 'M.O.' ? '#fef3c7' : '#eff6ff', color: l.prestation === 'M.O.' ? '#92400e' : '#1d4ed8', padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontSize: '11px' }}>{l.prestation || '—'}</span></td>
                        <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{l.designation || '—'}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: '11px' }}>{l.ref || '—'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{l.qte != null ? l.qte : '—'}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: parseFloat(l.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(l.montant_ht)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr style={{ background: 'var(--bg-muted)', borderTop: '2px solid var(--border-default)' }}>
                    <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', fontSize: '13px' }}>Total HT</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: '14px', textAlign: 'right', color: parseFloat(factureDetail.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht)}</td>
                  </tr></tfoot>
                </table>
              </>)}
            </div>
          </div>
        </div>
      )}

      {commandeDetailOr && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <h2>Détail commande — OR {commandeDetailOr}</h2>
              <button className="modal-close" onClick={() => setCommandeDetailOr(null)}>✕</button>
            </div>
            <div className="modal-body">
              {commandeDetailLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
              ) : !commandeDetailData?.found || !commandeDetailData?.lignes?.length ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucune ligne de commande pour cet OR</div>
              ) : (() => {
                const lignes = commandeDetailData.lignes;
                const allLivre = lignes.every(l => l.statut_livraison === 'livre');
                const anyLivre = lignes.some(l => l.statut_livraison !== 'en_commande');
                const statutOr = allLivre ? 'livre' : anyLivre ? 'recu_partiellement' : 'en_commande';
                return (
                  <>
                    <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
                      <strong>{commandeDetailData.nom_client || '—'}</strong>
                      {commandeDetailData.immat && <span> — {commandeDetailData.immat}</span>}
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: statutOr === 'livre' ? '#dcfce7' : statutOr === 'recu_partiellement' ? '#fef3c7' : '#dbeafe',
                        color: statutOr === 'livre' ? '#15803d' : statutOr === 'recu_partiellement' ? '#b45309' : '#1d4ed8',
                      }}>
                        {statutOr === 'livre' ? '✓ Tout reçu' : statutOr === 'recu_partiellement' ? '◐ Reçu partiellement' : '⏳ En commande'}
                      </span>
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>{lignes.length} ligne(s)</span>
                    </div>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Référence</th>
                            <th>Désignation</th>
                            <th>Fournisseur</th>
                            <th style={{ textAlign: 'center' }}>Qté cdée</th>
                            <th style={{ textAlign: 'center' }}>Qté livrée</th>
                            <th>Statut</th>
                            <th>Anomalie</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lignes.map((l, i) => (
                            <tr key={i}>
                              <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.reference || '—'}</td>
                              <td>{l.designation || l.description || '—'}</td>
                              <td>{l.fournisseur || '—'}</td>
                              <td style={{ textAlign: 'center' }}>{l.cde_qty || 0}</td>
                              <td style={{ textAlign: 'center' }}>{l.qte_livree || 0}</td>
                              <td>
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                                  background: l.statut_livraison === 'livre' ? '#dcfce7' : l.statut_livraison === 'livre_partiellement' ? '#fef3c7' : '#dbeafe',
                                  color: l.statut_livraison === 'livre' ? '#15803d' : l.statut_livraison === 'livre_partiellement' ? '#b45309' : '#1d4ed8',
                                }}>
                                  {l.statut_livraison === 'livre' ? '✓ Reçu' : l.statut_livraison === 'livre_partiellement' ? '◐ Partiel' : '⏳ Commandé'}
                                </span>
                              </td>
                              <td>
                                {l.anomalie_active ? (
                                  <div style={{ fontSize: 11 }}>
                                    <span style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: 4, fontWeight: 600, border: '1px solid #fecaca' }}>
                                      ⚠️ {l.anomalie_active.type || 'Anomalie'}
                                    </span>
                                    {l.anomalie_active.commentaire && (
                                      <div style={{ color: 'var(--text-secondary)', marginTop: 3, fontSize: 10, lineHeight: 1.3 }}>{l.anomalie_active.commentaire}</div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {modaleDossier}
    </div>
  );
}

export default AtelierView;
